import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ClientService,
  ContactService,
  StipulationService,
  TenantService,
  WhiteLabelsService,
} from '@core/services';
import { mapDefaultApplicationType } from '@shared/application-type-mapper.util';
import {
  applicationRequiredStipulations,
  applicationStipulationConfiguration,
} from '@v2/modules/applications/add-application/application.utils';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, skipWhile, switchMap } from 'rxjs/operators';

/**
 * Store for managing application state using reactive patterns
 */
@Injectable()
export class OiqApplicationAddStore {
  private tenantService = inject(TenantService);
  private whiteLabelsService = inject(WhiteLabelsService);
  private stipulationService = inject(StipulationService);
  private clientService = inject(ClientService);
  private contactService = inject(ContactService);

  public get tenantId(): number {
    return this.tenantId$.value;
  }

  /**
   * Do not use this if you want to force reload
   * use directly isoId$.next()
   */
  public set tenantId(v: number) {
    // normalize to number
    const normalized = v ? Number(v) : 0;
    if (this.tenantId$.value === normalized) {
      return;
    }
    this.tenantId$.next(normalized);
  }

  public get isoId(): any {
    return this.isoId$.value;
  }

  /**
   * Do not use this if you want to force reload
   * use directly isoId$.next()
   */
  public set isoId(v: any) {
    // normalize to number
    const normalized = v ? Number(v) : 0;
    if (this.isoId$.value === normalized) {
      return;
    }
    this.isoId$.next(normalized);
  }

  // Input streams
  tenantId$ = new BehaviorSubject<number>(0);
  clientId$ = new BehaviorSubject<number>(0);
  isoId$ = new BehaviorSubject<number>(0);

  // Derived streams
  tenantApplicationTypes$ = this.tenantId$.pipe(
    skipWhile(tenantId => !tenantId),
    switchMap(tenantId => (tenantId ? this.tenantService.getApplicationTypes(tenantId) : of([]))),
    map((response: any[]) =>
      response.map(item => ({
        name: mapDefaultApplicationType(item.type, item.isNative),
        value: item.type,
      }))
    ),
    shareReplay()
  );

  stipulations$ = this.tenantId$.pipe(
    skipWhile(tenantId => !tenantId),
    switchMap(tenantId =>
      tenantId ? this.stipulationService.list(tenantId) : of({ content: [] })
    ),
    map((response: any) => response.content),
    shareReplay()
  );

  whiteLabels$ = this.tenantId$.pipe(
    skipWhile(tenantId => !tenantId),
    switchMap(tenantId => (tenantId ? this.whiteLabelsService.list() : of({ content: [] }))),
    map((response: any) => response.content),
    shareReplay()
  );

  defaultWhiteLabel$ = this.whiteLabels$.pipe(
    map(_whiteLabels => _whiteLabels.find(item => item.tenantDefault === true))
  );

  tenantSettings$: Observable<{
    fundingOnly: boolean;
    features: { [feature: string]: boolean };
    excludeFromCreditCheck: boolean;
  }> = this.tenantId$.pipe(
    skipWhile(tenantId => !tenantId),
    switchMap(tenantId => (tenantId ? this.tenantService.getSettings(tenantId) : of(null))),
    map((response: any) =>
      response
        ? {
            fundingOnly: response.fundingOnly as boolean,
            features: response.features,
            excludeFromCreditCheck: !!response.excludeFromCreditCheck,
          }
        : null
    ),
    shareReplay()
  );

  hasCreditReport$: Observable<boolean> = this.tenantSettings$.pipe(
    map(settings => settings.features['Experian'] || settings.features['Crs'])
  );
  hasCreditReport = toSignal(this.hasCreditReport$);

  requiredStipulations$ = this.stipulations$.pipe(
    map(applicationRequiredStipulations),
    shareReplay()
  );

  applicationStipulations$ = this.requiredStipulations$.pipe(
    map((stipulations: any[]) => stipulations.map(stipulation => stipulation.name as string)),
    shareReplay()
  );

  options$ = this.stipulations$.pipe(map(applicationStipulationConfiguration), shareReplay());

  clientData$ = this.clientId$.pipe(
    distinctUntilChanged((a, b) => a == b),
    skipWhile(clientId => !clientId),
    switchMap(clientId => (clientId ? this.clientService.getById(clientId) : of(null))),
    shareReplay()
  );

  clientContacts$ = this.clientId$.pipe(
    distinctUntilChanged((a, b) => a == b),
    skipWhile(clientId => !clientId),
    switchMap(clientId =>
      clientId ? this.contactService.getContactsByClientId(clientId) : of({ content: [] })
    ),
    map((response: any) => response.content as any[]),
    shareReplay()
  );
}
